
import { useMemo } from 'react';
import {
    RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis
} from 'recharts';
import type { Employee, CategoryStats } from '../../types/results';
import { getHebrewName } from '../../constants/muscles';
import { DEMOGRAPHIC_COLORS, DEMOGRAPHIC_LABELS } from '../../constants/demographics';

interface PercentileViewProps {
    employee: Employee;
    globalPercentiles?: CategoryStats; // Dynamic global percentiles from SQL
    instancePercentiles?: CategoryStats; // Instance/Group percentiles from SQL (priority)
    isOrgView?: boolean;
    hideTeam?: boolean;
    ageGroupPercentiles?: CategoryStats | null;
    genderGroupPercentiles?: CategoryStats | null;
}

const COLORS = {
    org: '#133437',
    global: '#c17d5f',
    bg: '#f4f1eb'
};

export default function PercentileView({ employee, globalPercentiles, instancePercentiles, isOrgView, hideTeam, ageGroupPercentiles, genderGroupPercentiles }: PercentileViewProps) {
    const categories = Object.keys(employee.scores);

    const benchmarkLabel = isOrgView ? 'ארגוני' : 'קבוצתי';
    const hasDemographicPercentiles = !!ageGroupPercentiles || !!genderGroupPercentiles;

    const categoryData = useMemo(() => {
        return categories.map(cat => {
            // Instance/Group Percentile (from backend)
            // Prefer explicitly passed instancePercentiles (from full respondent data)
            const orgPercentile = instancePercentiles?.[cat] ?? employee.instancePercentiles?.[cat] ?? 0;

            // Global Percentile (from backend)
            const globalPercentile = globalPercentiles?.[cat] ?? 0;

            // Demographic percentiles
            const agePercentile = ageGroupPercentiles?.[cat];
            const genderPercentile = genderGroupPercentiles?.[cat];

            // Chart data - exclude instance percentile when hideTeam is true
            const chartData: { name: string; value: number; fill: string }[] = [];

            chartData.push({ name: 'אחוזון גלובלי', value: globalPercentile, fill: COLORS.global });

            if (!hideTeam) {
                chartData.push({ name: `אחוזון ${benchmarkLabel}`, value: orgPercentile, fill: COLORS.org });
            }

            if (agePercentile !== undefined) {
                chartData.push({ name: DEMOGRAPHIC_LABELS.ageGroup, value: agePercentile, fill: DEMOGRAPHIC_COLORS.ageGroup });
            }

            if (genderPercentile !== undefined) {
                chartData.push({ name: DEMOGRAPHIC_LABELS.genderGroup, value: genderPercentile, fill: DEMOGRAPHIC_COLORS.genderGroup });
            }

            return {
                id: cat,
                name: getHebrewName(cat),
                orgPercentile,
                globalPercentile,
                agePercentile,
                genderPercentile,
                chartData
            };
        });
    }, [employee, globalPercentiles, instancePercentiles, ageGroupPercentiles, genderGroupPercentiles, categories, benchmarkLabel, hideTeam]);

    return (
        <div className="analytics-radial-grid-container" style={{ direction: 'rtl', width: '100%', padding: '8px 0' }}>
            {/* Legend section */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                marginBottom: '20px',
                padding: '0 8px',
                flexWrap: 'wrap',
                gap: '24px'
            }}>
                <div style={{
                    display: 'flex',
                    gap: '20px',
                    fontSize: '11px',
                    fontWeight: 700,
                    backgroundColor: 'var(--neutral-50)',
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: '1px solid var(--neutral-100)',
                    flexWrap: 'wrap'
                }}>
                    {!hideTeam && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: COLORS.org }}></div>
                            <span style={{ color: COLORS.org }}>אחוזון {benchmarkLabel}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: COLORS.global }}></div>
                        <span style={{ color: COLORS.global }}>אחוזון גלובלי</span>
                    </div>
                    {ageGroupPercentiles && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: DEMOGRAPHIC_COLORS.ageGroup }}></div>
                            <span style={{ color: DEMOGRAPHIC_COLORS.ageGroup }}>{DEMOGRAPHIC_LABELS.ageGroup}</span>
                        </div>
                    )}
                    {genderGroupPercentiles && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: DEMOGRAPHIC_COLORS.genderGroup }}></div>
                            <span style={{ color: DEMOGRAPHIC_COLORS.genderGroup }}>{DEMOGRAPHIC_LABELS.genderGroup}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Grid container for cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '20px',
                width: '100%'
            }}>
                {categoryData.map((cat) => (
                    <div key={cat.id} className="radial-card" style={{
                        backgroundColor: '#fff',
                        borderRadius: '20px',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02)',
                        border: '1px solid var(--neutral-100)',
                        transition: 'all 0.3s ease',
                        cursor: 'default',
                        minHeight: hasDemographicPercentiles ? '260px' : '220px'
                    }}>
                        {/* Gauge container with improved sizing */}
                        <div style={{ width: '130px', height: '130px', position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <RadialBarChart
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={hasDemographicPercentiles ? '55%' : '70%'}
                                    outerRadius="100%"
                                    barSize={hasDemographicPercentiles ? 7 : 10}
                                    data={cat.chartData}
                                    startAngle={90}
                                    endAngle={450}
                                >
                                    <PolarAngleAxis
                                        type="number"
                                        domain={[0, 100]}
                                        angleAxisId={0}
                                        tick={false}
                                    />
                                    <RadialBar
                                        background={{ fill: COLORS.bg }}
                                        dataKey="value"
                                        cornerRadius={5}
                                        isAnimationActive={true}
                                    />
                                </RadialBarChart>
                            </ResponsiveContainer>

                            {/* Central Percentile Text */}
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                                paddingBottom: '2px'
                            }}>
                                {!hideTeam && (
                                    <div style={{
                                        fontSize: hasDemographicPercentiles ? '14px' : '18px',
                                        fontWeight: 900,
                                        color: COLORS.org,
                                        lineHeight: '1.2'
                                    }}>
                                        {cat.orgPercentile}%
                                    </div>
                                )}
                                <div style={{
                                    fontSize: (hideTeam && !hasDemographicPercentiles) ? '18px' : '12px',
                                    fontWeight: 900,
                                    color: COLORS.global,
                                    lineHeight: '1.2'
                                }}>
                                    {cat.globalPercentile}%
                                </div>
                            </div>
                        </div>

                        {/* Demographic percentile badges below the gauge */}
                        {hasDemographicPercentiles && (
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                marginTop: '8px',
                                flexWrap: 'wrap',
                                justifyContent: 'center'
                            }}>
                                {cat.agePercentile !== undefined && (
                                    <span style={{
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        color: DEMOGRAPHIC_COLORS.ageGroup,
                                        backgroundColor: `${DEMOGRAPHIC_COLORS.ageGroup}15`,
                                        padding: '2px 6px',
                                        borderRadius: '6px',
                                    }}>
                                        גיל: {cat.agePercentile}%
                                    </span>
                                )}
                                {cat.genderPercentile !== undefined && (
                                    <span style={{
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        color: DEMOGRAPHIC_COLORS.genderGroup,
                                        backgroundColor: `${DEMOGRAPHIC_COLORS.genderGroup}15`,
                                        padding: '2px 6px',
                                        borderRadius: '6px',
                                    }}>
                                        מגדר: {cat.genderPercentile}%
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Category Label with Wrapping fix */}
                        <div style={{
                            marginTop: '16px',
                            fontSize: '13px',
                            fontWeight: 700,
                            color: 'var(--neutral-700)',
                            textAlign: 'center',
                            width: '100%',
                            lineHeight: '1.4',
                            whiteSpace: 'normal', /* Allow text wrapping */
                            overflow: 'visible',  /* Show all text */
                            display: 'block'
                        }}>
                            {cat.name}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
